import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Search, X } from "lucide-react";
import { listClients, listIngredients, listOrders, listRecipes } from "@/lib/masterOrderApi";

// Existing Master Order APIs enforce the current user's regional access.
// This is a scoped search, not a cross-region or cross-product index.
const normal = (value) => String(value ?? "").toLocaleLowerCase("ru-RU");
const matches = (query, ...values) => values.some((value) => normal(value).includes(query));
const asRows = (value) => Array.isArray(value) ? value : [];
const SECTIONS = [
  { label: "Заказы", path: "/master-order/orders" },
  { label: "Клиенты", path: "/master-order/clients" },
  { label: "Мои рецептуры", path: "/master-order/recipes" },
  { label: "Ингредиенты", path: "/master-order/ingredients" },
  { label: "Список покупок", path: "/master-order/purchases" },
  { label: "Производство", path: "/master-order/production" },
  { label: "Календарь", path: "/master-order/calendar" },
  { label: "Себестоимость", path: "/master-order/product-cost" },
  { label: "Упаковка", path: "/master-order/packaging-items" },
  { label: "Справочник мер", path: "/master-order/measure-directory" },
];

export function buildResults(values, query) {
  const [orders, clients, recipes, ingredients] = values.map(asRows);
  const clientNames = new Map(clients.map((client) => [client.id, client.name || ""]));
  return [
    {
      title: "Разделы",
      items: SECTIONS.filter((section) => matches(query, section.label))
        .map((section) => ({ title: section.label, path: section.path })),
    },
    {
      title: "Заказы",
      items: orders.filter((order) => matches(
        query, order.order_number, clientNames.get(order.client_id),
        order.wishes, order.design_notes
      )).map((order) => ({
        title: "Заказ №" + (order.order_number || "—"),
        detail: clientNames.get(order.client_id) || "",
        path: "/master-order/orders/" + encodeURIComponent(order.id),
      })),
    },
    {
      title: "Клиенты",
      items: clients.filter((client) => matches(
        query, client.name, client.phone, client.email, client.messenger
      )).map((client) => ({
        title: client.name || "Клиент",
        path: "/master-order/clients/" + encodeURIComponent(client.id),
      })),
    },
    {
      title: "Рецептуры",
      items: recipes.filter((recipe) => recipe.is_active !== false
        && matches(query, recipe.name, recipe.category)).map((recipe) => ({
        title: recipe.name || "Рецептура",
        detail: recipe.category || "",
        path: "/master-order/recipes?search=" + encodeURIComponent(query),
      })),
    },
    {
      title: "Ингредиенты",
      items: ingredients.filter((ingredient) => ingredient.is_active !== false
        && matches(query, ingredient.name, ingredient.category)).map((ingredient) => ({
        title: ingredient.name || "Ингредиент",
        detail: ingredient.category || "",
        path: "/master-order/ingredients?search=" + encodeURIComponent(query),
      })),
    },
  ];
}

export default function MOTopbarSearch() {
  const nav = useNavigate();
  const ticket = useRef(0);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("idle");
  const [groups, setGroups] = useState([]);
  const [failed, setFailed] = useState([]);
  const [mobileSearch, setMobileSearch] = useState(() => window.matchMedia("(max-width: 767px)").matches);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const sync = () => setMobileSearch(media.matches);
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  useEffect(() => () => { ticket.current += 1; }, []);

  const reset = (value = "") => {
    ticket.current += 1;
    setQuery(value);
    setStatus("idle");
    setGroups([]);
    setFailed([]);
  };

  const submit = async (event) => {
    event.preventDefault();
    const searchText = query.trim();
    if (!searchText) return;
    const current = ++ticket.current;
    setStatus("loading");
    setFailed([]);
    const sources = [
      { label: "заказы", read: listOrders },
      { label: "клиенты", read: listClients },
      { label: "рецептуры", read: listRecipes },
      { label: "ингредиенты", read: listIngredients },
    ];
    const settled = await Promise.allSettled(sources.map((source) => source.read()));
    if (current !== ticket.current) return;
    const failedSources = settled.flatMap((result, index) =>
      result.status === "rejected" ? [sources[index].label] : []);
    const values = settled.map((result) => result.status === "fulfilled" ? result.value : []);
    setGroups(buildResults(values, normal(searchText)));
    setFailed(failedSources);
    setStatus(failedSources.length === sources.length ? "error" : "ready");
  };

  const open = (path) => {
    reset();
    nav(path);
  };
  const count = groups.reduce((total, group) => total + group.items.length, 0);

  return (
    <div className="relative w-full" data-testid="mo-global-search">
      <form role="search" onSubmit={submit}>
        <div className="flex min-h-10 items-center gap-2 rounded-xl bg-white px-2.5"
          style={{ boxShadow: "inset 0 0 0 1px #EFE4D0" }}>
          <Search size={16} className="shrink-0 text-[#8B6F4B]" aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(event) => reset(event.target.value)}
            placeholder={mobileSearch ? "Поиск" : "Заказы, клиенты, рецептуры, ингредиенты…"}
            aria-label="Поиск по Мастеру заказа"
            data-testid="mo-topbar-search"
            className="min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-[#A18E79]"
          />
          {query && (
            <button type="button" onClick={() => reset()} aria-label="Очистить поиск"
              data-testid="mo-search-clear" className="grid h-8 w-8 shrink-0 place-items-center text-[#755E48]">
              <X size={14} />
            </button>
          )}
          <button type="submit" disabled={!query.trim() || status === "loading"}
            data-testid="mo-search-submit"
            className="min-h-9 shrink-0 rounded-lg bg-[#4A3728] px-3 text-[12px] text-[#FFF9ED] disabled:opacity-40">
            {status === "loading" ? <Loader2 size={15} className="animate-spin" aria-label="Ищем" /> : "Найти"}
          </button>
        </div>
      </form>
      {status !== "idle" && (
        <div role="region" aria-label="Результаты поиска по Мастеру заказа"
          data-testid="mo-search-results"
          className="absolute left-0 right-0 top-full z-50 mt-2 max-h-[60vh] overflow-y-auto rounded-xl border border-[#E5D7C4] bg-[#FFFDF9] p-3 shadow-xl">
          {status === "loading" && <p role="status" className="text-[13px] text-[#6F5A46]">Ищем в Ваших данных…</p>}
          {status === "error" && (
            <p role="alert" className="text-[13px] text-[#8A3F1E]">
              Не удалось выполнить поиск. Проверьте соединение и повторите запрос.
            </p>
          )}
          {status === "ready" && (
            <>
              <p role="status" className="mb-2 text-[12px] text-[#765E48]">
                {count ? "Найдено: " + count : "По этому запросу ничего не найдено"}
              </p>
              {groups.filter((group) => group.items.length).map((group) => (
                <section key={group.title} className="mb-3 last:mb-0">
                  <h3 className="mb-1 text-[11px] font-semibold text-[#674C36]">{group.title}</h3>
                  {group.items.slice(0, 20).map((item, index) => (
                    <button key={item.path + ":" + index} type="button"
                      onClick={() => open(item.path)}
                      className="flex min-h-10 w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left hover:bg-[#F4ECE0] focus-visible:outline-2 focus-visible:outline-[#8B6F4B]">
                      <span className="min-w-0 truncate text-[13px] text-[#35281E]">{item.title}</span>
                      {item.detail && <span className="max-w-[38%] truncate text-[11px] text-[#8B6F4B]">{item.detail}</span>}
                    </button>
                  ))}
                  {group.items.length > 20 && (
                    <p className="px-3 text-[11px] text-[#8B6F4B]">Показаны первые 20. Уточните запрос.</p>
                  )}
                </section>
              ))}
              {failed.length > 0 && (
                <p role="alert" className="mt-2 text-[12px] text-[#8A3F1E]">
                  Часть данных недоступна: {failed.join(", ")}. Результаты могут быть неполными.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
